<template>
  <div class="container-custom moo-sheet-page pb-page" :style="styleObject" v-if="isLoadDone">
    <div class="content-custom">
      <div class="pb-shell">
        <div class="pb-brand">
          <img v-if="merchantLogo" class="pb-brand__logo" :src="merchantLogo" alt="Logo" />
          <span class="pb-brand__name">{{ brandName }}</span>
        </div>

        <!-- SF307: merchant-uploaded header, fixed 1080 x 1440 (3:4). -->
        <img v-if="headerImage" class="pb-header-img" :src="headerImage" alt="" />

        <div class="pb-body">
          <div class="pb-search">
            <span class="material-icons-round">search</span>
            <input
              v-model="searchKeyword"
              type="search"
              id="keyword_search"
              name="keyword_search"
              placeholder="Search for your branches"
              autocomplete="off"
              @input="getBranches"
            />
          </div>

          <!-- SF307: one card per sale channel, styled after the salon location picker
               (src/salon/views/SelectLocation.vue): pick a card, then Continue. -->
          <div class="pb-list" v-if="channels.length" role="radiogroup">
            <section v-for="group in groups" :key="group.id" class="pb-group">
            <h3 class="pb-group__name">
              <span class="material-icons-round">location_on</span>{{ group.name }}
            </h3>
            <label
              v-for="channel in group.channels"
              :key="channel.id"
              class="pb-card"
              :class="{
                'pb-card--selected': channel.branch_code === selectedCode,
                'pb-card--closed': !channel.is_open,
              }"
            >
              <input
                v-model="selectedCode"
                class="pb-card__radio"
                type="radio"
                name="pb_channel"
                :value="channel.branch_code"
              />
              <div class="pb-card__media">
                <img v-if="channel.icon_url" :src="channel.icon_url" :alt="channel.branch_name" />
                <span v-else class="pb-card__initials">{{ channel.initials }}</span>
              </div>
              <span v-if="channel.branch_code === selectedCode" class="pb-card__check">
                <span class="material-icons-round">check</span>
              </span>
              <div class="pb-card__body">
                <div class="pb-card__name">{{ channel.branch_name }}</div>
                <div class="pb-card__address" v-if="channel.addressLine">{{ channel.addressLine }}</div>
                <div class="pb-card__foot">
                  <span class="pb-card__status" v-if="channel.open_status_text">
                    <span class="pb-card__dot"></span>{{ channel.open_status_text }}
                  </span>
                  <button
                    v-if="channel.destination_link"
                    type="button"
                    class="pb-card__map"
                    @click.prevent.stop="openMap(channel.destination_link)"
                  >
                    <span class="material-icons-outlined">map</span>
                    View Map
                  </button>
                </div>
              </div>
            </label>
            </section>
          </div>

          <div class="pb-empty" v-else>
            <span class="material-icons-outlined">store_mall_directory</span>
            <span>No branches found</span>
          </div>
        </div>

        <nav class="pb-cta" v-if="channels.length">
          <button type="button" class="pb-cta__btn" :disabled="!selectedCode" @click="clickLink(selectedCode)">
            Continue
            <span class="material-icons-round">arrow_forward</span>
          </button>
        </nav>
      </div>
    </div>
  </div>
  <div
      class="position-fixed top-1 end-1 message-success"
      style="z-index: 99999 !important"
    >
      <material-snackbar
        v-if="snackbarAsMember === 'danger' || snackbarAsMember === 'success'"
        title=""
        date=""
        :description="messageAsMember"
        :icon="{ component: 'campaign', color: 'white' }"
        :class="{
          'bg-danger text-white': snackbarAsMember === 'danger',
          'bg-success text-white': snackbarAsMember === 'success'
        }"
        :close-handler="closeSnackbarAsMember"
      />
    </div>
    <material-loading :active="loaderActive" />
</template>

<script>
import MaterialSnackbar from "@/components/MaterialSnackbar.vue";
import MaterialLoading from "@/components/MaterialLoading.vue";
import OrderService from "@/services/order.service";
import { localStorageName } from "@/config/utils";
import { mapActions } from "vuex";

export default {
  name: "PhysicalBranch",
  components: {
    MaterialSnackbar,
    MaterialLoading,
  },
  data() {
    return {
      isLoadDone: false,
      snackbarAsMember: null,
      messageAsMember: "",
      loaderActive: false,
      branches: [],
      snackbar: null,
      message: "",
      physicalBranch: "",
      searchKeyword: "",
      searchTimerId: null,
      merchantCode: "",
      physicalBranchId: "",
      merchantLogo: "",
      brandName: "",
      headerImage: "",
      selectedCode: "",
    };
  },
  async created() {
    this.isLoadDone = false;
    this.merchantCode = this.$route.params.merchant_code;
    if (this.merchantCode) {
      await this.getBranches();
    }
    this.isLoadDone = true;
  },
  computed: {
    // SF307: the cards list sale channels, not outlets. The outlet's address stands in
    // when the channel has none of its own; the badge initials come from the outlet name
    // when it has a single channel (SONGFA's "Collection @ ..." all start alike), else
    // from the channel name so sibling channels stay distinguishable.
    // Grouped under their physical branch; an outlet with no channel has nothing to
    // pick, so it gets no heading either.
    groups() {
      return this.branches
        .filter((outlet) => outlet.virtual_branches?.length)
        .map((outlet) => {
          const list = outlet.virtual_branches;
          return {
            id: outlet.id,
            name: outlet.name,
            channels: list.map((channel) => ({
              ...channel,
              addressLine: channel.address_text || outlet.address || "",
              initials: this.initials(list.length === 1 ? outlet.name : channel.branch_name),
            })),
          };
        });
    },
    channels() {
      return this.groups.flatMap((group) => group.channels);
    },
    styleObject() {
      return {
        "--color": this.$store.state.color,
      };
    },
  },
  methods: {
    ...mapActions(["setColor", "setMerchantCode", "setBranchCode", "setError"]),

    onChangesearchKeyword(event) {
      this.searchKeyword = event.target.value;
      clearTimeout(this.searchTimerId);
      this.searchTimerId = setTimeout(() => {
        this.getBranches();
      }, 1000);
    },

    async getBranches() {
      this.showLoader();
      let dataForm = {
        search_keyword: this.searchKeyword
      };
      await OrderService.getPhysicalBranches(dataForm)
        .then((response) => {
          if (response.data.result.isSuccess) {
            this.branches = response.data.data.branches;
            this.setColor(response.data.data.moo_color ?? "#f9dd4b");
            this.setMerchantCode(this.merchantCode ?? "");
            localStorage.setItem(localStorageName("MERCHANT_LOGO"), response.data.data.merchant_logo ?? "");
            localStorage.setItem(localStorageName("MERCHANT_BRAND_NAME"), response.data.data.merchant_brand_name ?? "");
            this.merchantLogo = response.data.data.merchant_logo ?? "";
            this.brandName = response.data.data.merchant_brand_name ?? "";
            this.headerImage = response.data.data.outlet_list_header_image ?? "";
          } else {
            this.snackbarAsMember = "danger";
            this.messageAsMember = response.data.result.message;
            this.setTimeOutNotification();
          }
        })
        .catch((error) => {
          this.snackbarAsMember = "danger";
          this.messageAsMember = error.message || "Network error";
          this.setTimeOutNotification();
        })
        .finally(() => {
          this.hideLoader();
        });
    },

    setTimeOutNotification() {
      setTimeout(() => {
        this.snackbarAsMember = null;
      }, 3000);
    },

    showLoader() {
      this.loaderActive = true;
    },

    hideLoader() {
      this.loaderActive = false;
    },

    closeSnackbarAsMember() {
      this.snackbarAsMember = null;
    },

    initials(name) {
      return (name || "")
        .split(/[^A-Za-z0-9]+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((word) => word[0].toUpperCase())
        .join("");
    },

    openMap(link) {
      window.open(link, "_blank", "noopener");
    },

    clickLink(branchCode) {
      if (branchCode && branchCode != "") {
        const routeData = this.$router.resolve({ name: 'Cover', params: { merchant_code: this.merchantCode, branch_code: branchCode } });
        window.open(routeData.href, '_self');
      }
    },
  },
};
</script>

<style scoped>
/* SF307: same locked 480px column as the theme-2 MOO screens (.mlx-landing,
   .moo-sheet-shell), so desktop renders exactly like mobile. Colour tokens
   (--sheet*) come from assets/css/moo-sheet.css. */
/* .pb-page prefix outranks moo-sheet.css's `.moo-sheet-page .content-custom`
   cream fill: the sides outside the column stay white, as on MOO. */
.pb-page,
.pb-page .content-custom {
  min-height: 100%;
  background: #ffffff;
}
.pb-shell {
  max-width: 480px;
  min-height: 100vh;
  min-height: 100svh;
  margin: 0 auto;
  background: var(--sheet-bg);
}

.pb-brand {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 20px;
  background: #ffffff;
  border-bottom: 1px solid var(--sheet-border);
}
.pb-brand__logo {
  width: 40px;
  height: 40px;
  border-radius: 12px;
  object-fit: cover;
}
.pb-brand__name {
  font-size: 16px;
  font-weight: 800;
  color: #241a12;
  letter-spacing: -0.01em;
}

.pb-header-img {
  display: block;
  width: 100%;
  aspect-ratio: 3 / 4;
  object-fit: cover;
}

.pb-body {
  padding: 20px 20px calc(110px + env(safe-area-inset-bottom, 0px));
}

.pb-search {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 14px;
  height: 46px;
  margin-bottom: 16px;
  background: #ffffff;
  border: 1px solid var(--sheet-border);
  border-radius: 9999px;
}
.pb-search:focus-within {
  border-color: var(--sheet);
}
.pb-search .material-icons-round {
  font-size: 20px;
  color: #857372;
}
.pb-search input {
  flex: 1;
  min-width: 0;
  height: 100%;
  border: none !important;
  outline: none;
  background: transparent;
  font-size: 14px;
  color: #241a12;
  padding: 0;
}
.pb-search input::placeholder {
  color: #b3a7a5;
}

.pb-list {
  display: flex;
  flex-direction: column;
  gap: 22px;
}
.pb-group {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.pb-group__name {
  display: flex;
  align-items: center;
  gap: 4px;
  margin: 0;
  /* the theme gives h3 its serif heading face */
  font-family: inherit;
  font-size: 13px;
  font-weight: 800;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #5c534f;
}
.pb-group__name .material-icons-round {
  font-size: 18px;
  color: var(--sheet);
}
.pb-card {
  position: relative;
  display: flex;
  align-items: stretch;
  margin: 0;
  background: #ffffff;
  border: 1.5px solid transparent;
  border-radius: 20px;
  box-shadow: 0 2px 12px rgba(36, 26, 18, 0.06);
  overflow: hidden;
  cursor: pointer;
}
.pb-card:active {
  transform: scale(0.99);
}
.pb-card--selected {
  border-color: var(--sheet);
}
.pb-card__radio {
  position: absolute;
  opacity: 0;
  pointer-events: none;
}
/* Narrower than the salon card's media so the channel name gets the width. */
.pb-card__media {
  flex: 0 0 64px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #ebe8e4;
}
.pb-card__media img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.pb-card__initials {
  font-size: 18px;
  font-weight: 800;
  color: #5c534f;
  letter-spacing: 0.02em;
}
.pb-card__body {
  flex: 1;
  min-width: 0;
  padding: 14px 14px 12px;
}
.pb-card__name {
  font-size: 15px;
  font-weight: 800;
  line-height: 1.3;
  color: #241a12;
  word-break: break-word;
}
.pb-card__address {
  margin-top: 3px;
  font-size: 12px;
  line-height: 1.45;
  color: #857372;
  word-break: break-word;
}
.pb-card__foot {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 10px;
  min-height: 30px;
}
.pb-card__status {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 700;
  color: #1e8a5a;
  white-space: nowrap;
}
.pb-card__dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: currentColor;
}
.pb-card--closed .pb-card__status {
  color: #a29491;
}
.pb-card__map {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-left: auto;
  height: 30px;
  padding: 0 10px;
  background: #ffffff;
  border: 1px solid #e3dedb;
  border-radius: 9999px;
  font-size: 12px;
  font-weight: 600;
  color: #3a302b;
  white-space: nowrap;
  cursor: pointer;
}
.pb-card__map .material-icons-outlined {
  font-size: 15px;
}
/* Top-right corner, so the status line and View Map keep their full row. */
.pb-card__check {
  position: absolute;
  top: 12px;
  right: 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: var(--sheet);
  color: #ffffff;
}
.pb-card--selected .pb-card__name {
  padding-right: 30px;
}
.pb-card__check .material-icons-round {
  font-size: 18px;
}

/* Fixed to the viewport but held inside the 480px column. */
.pb-cta {
  position: fixed;
  left: 50%;
  bottom: 0;
  transform: translateX(-50%);
  width: 100%;
  max-width: 480px;
  padding: 12px 20px calc(16px + env(safe-area-inset-bottom, 0px));
  background: linear-gradient(to bottom, rgba(255, 255, 255, 0), var(--sheet-bg) 30%);
  z-index: 10;
}
.pb-cta__btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  height: 56px;
  border: none;
  border-radius: 9999px;
  background: #1c1b1b;
  color: #ffffff;
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
}
.pb-cta__btn:disabled {
  opacity: 0.35;
  cursor: default;
}
.pb-cta__btn .material-icons-round {
  font-size: 20px;
}

.pb-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 40px 0;
  color: #857372;
  font-size: 13px;
}
.pb-empty .material-icons-outlined {
  font-size: 28px;
  color: var(--sheet-border);
}
</style>
